<template>
  <form v-if="product">
    <h3 class="mb-2" v-if="product._id">
      Edit app "{{ product.slotGeneral.name }}"
      <span class="badge badge-warning" title="Status of app">Draft</span>
    </h3>
    <h3 class="mb-2" v-if="!product._id">Add App</h3>
    <div class="alert alert-danger" role="alert" v-show="message">
      {{ message }}
    </div>
    <wizard ref="wizard" :steps="steps" :onNext="nextWizard" :onBack="backWizard" :onSubmit="submitApp">
      <!-- General page -->
      <div slot="slotGeneral">
        <div class="row m-0 p-0 b-1">
          <div class="col-lg-12 p-20">
            <!-- Application name -->
            <div class="form-group row m-b-15">
              <label for="name-input" class="col-lg-3 col-form-label">App name</label>
              <div class="col-lg-7">
                <input class="form-control" type="text" v-model="product.slotGeneral.name" id="name-input" @input="$v.product.slotGeneral.name.$touch()">
                <div v-if="$v.product.slotGeneral.name.$error" :class="{'invalid-feedback': $v.product.slotGeneral.name.$error}">This field is required. Please be sure to check.</div>
                <small class="form-text text-muted">What is the name of your app? (required, 5-30 characters)</small>
              </div>
            </div>
            <!-- Application file -->
            <div class="form-group row m-b-15">
              <label for="name-input" class="col-lg-3 col-form-label">App file</label>
              <div class="col-lg-7">
                <app-file-input class="app-file" :id="'file-input-app'" :type="'file'" :width="'150px'" :height="'70px'" v-model="product.slotGeneral.app"></app-file-input>
                <div v-if="$v.product.slotGeneral.app.$error" :class="{'invalid-feedback': $v.product.slotGeneral.app.$error}">This field is required (.zip file). Please be sure to check.</div>
                <small class="form-text text-muted">Upload the last version of your app (.zip required)</small>
              </div>
            </div>
            <!-- Application version -->
            <div class="form-group row m-b-15">
              <label for="version-input" class="col-lg-3 col-form-label">App version</label>
              <div class="col-lg-7">
                <input class="form-control" type="text" v-model="product.slotGeneral.technicalDetails.version" id="version-input" @input="$v.product.slotGeneral.technicalDetails.version.$touch()">
                <div v-if="$v.product.slotGeneral.technicalDetails.version.$error" :class="{'invalid-feedback': $v.product.slotGeneral.technicalDetails.version.$error}">This field is required. Please be sure to check.</div>
                <small class="form-text text-muted">Add the last version (required)</small>
              </div>
            </div>
            <!-- Certifications -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label">Certifications</label>
              <div class="col-lg-7">
                <div class="checkbox checkbox-css checkbox-success p-r-10">
                  <input id="check_ce" class="form-check-input" type="checkbox" v-model="product.slotGeneral.certifications" value="ce">
                  <label for="check_ce">CE</label>
                </div>
                <div class="checkbox checkbox-css checkbox-success">
                  <input id="check_fda" class="form-check-input" type="checkbox" v-model="product.slotGeneral.certifications" value="fda">
                  <label for="check_fda">FDA</label>
                </div>
                <div><small class="form-text text-muted">Is your app certified?</small></div>
              </div>
            </div>
            <!-- Viewer -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label">Viewer</label>
              <div class="col-lg-7">
                <div class="checkbox checkbox-css checkbox-success p-r-10">
                  <input id="viewer_orx" class="form-check-input" type="checkbox" v-model="product.slotGeneral.viewer" value="osirix">
                  <label for="viewer_orx">Osirix</label>
                </div>
                <div class="checkbox checkbox-css checkbox-success">
                  <input id="viewer_hrx" class="form-check-input" type="checkbox" v-model="product.slotGeneral.viewer" value="horos">
                  <label for="viewer_hrx">Horos</label>
                </div>
                <div class="checkbox checkbox-css checkbox-success">
                  <input id="none" class="form-check-input" type="checkbox" v-model="product.slotGeneral.viewer" value="none">
                  <label for="none">None</label>
                </div>
                <!--div class="form-check form-check-inline">
                  <label class="form-check-label">
                    <input class="form-check-input" type="radio" name="viewer" v-model="product.slotGeneral.viewer" value="standalone"> Standalone
                  </label>
                </div-->
                <div v-if="$v.product.slotGeneral.viewer.$error" :class="{'invalid-feedback': $v.product.slotGeneral.viewer.$error}">This field is required. Please be sure to check.</div>
                <div><small class="form-text text-muted">Select which viewer should be use with your app</small></div>
              </div>
            </div>
            <!-- Is App already distributed? -->
            <div class="form-group row m-b-15">
              <label for="link-external-distributed-input" class="col-lg-3 col-form-label">Is your app already distributed online?</label>
              <div class="col-lg-7">
                <div class="radio radio-css radio-success p-r-10">
                  <input id="radio_y" class="form-check-input" type="radio" name="externaldistributed" value="yes" v-model="externaldistributed">
                  <label for="radio_y">Yes</label>
                </div>
                <div class="radio radio-css radio-success">
                  <input id="radio_n" class="form-check-input" type="radio" name="externaldistributed" value="no" v-model="externaldistributed">
                  <label for="radio_n">No</label>
                </div>
                <div class="form-input">
                  <input class="form-control" id="is-already-distributed-input" placeholder="" type="url" v-model="product.slotGeneral.externalLinkDist">
                </div>
                <div v-if="$v.product.slotGeneral.externalLinkDist.$error" :class="{'invalid-feedback': $v.product.slotGeneral.externalLinkDist.$error}">This field is required. Please be sure to check.</div>
                <small class="form-text text-muted">If yes add internet link here (required)</small>
              </div>
            </div>
            <!-- Application price -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label">App price</label>
              <div class="col-lg-7">
                <div class="radio radio-css radio-success">
                  <input class="form-check-input" type="radio" name="appPrice" v-model="product.slotGeneral.price" value="0">
                  <label class="form-check-label">Free App</label>
                </div>
                <div><small class="form-text text-muted">In current version, you can add only Free App</small></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Images page -->
      <div slot="slotImages">
        <div class="row m-0 p-0 b-1">
          <div class="col-12 p-20">
            <!-- Logo -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label">App logo</label>
              <div class="col-lg-7">
                <div class="logo card text-center">
                  <img :src="imgLogoData.src">
                  <div class="card-footer">
                    <a class="btn btn-link" @click="showToUploadLogo">{{ (imgLogoData.hasImg) ? 'Change App logo' : 'Set App logo' }}</a>
                  </div>
                </div>
                <media-file-input class="app-media" field="app-logo" @crop-success="cropLogoSuccess" @crop-upload-success="cropLogoUploadSuccess" v-model="showLogoInput" :width="300" :height="300" :url="uploadUrl" :no-circle="true" lang-type="en" :headers="headers" img-format="png"></media-file-input>
                <div v-if="$v.product.slotImages.logo.$error" :class="{'invalid-feedback': $v.product.slotImages.logo.$error}">This field is required. Please be sure to check.</div>
                <small class="form-text text-muted">Upload the logo of your app (required)</small>
              </div>
            </div>
            <!-- Screenshots -->
            <div class="form-group row m-b-15" v-if="product.slotImages.screenshots">
              <label class="col-lg-3 col-form-label">Screenshots ({{ product.slotImages.screenshots.length }})</label>
              <div class="col-lg-9">
                <div class="screenshots-list">
                  <div class="screenshots m-r-5" v-for="(image, imageIndex) in product.slotImages.screenshots" :key="image.slug">
                    <img class="d-block img-thumbnail" :src="image.slug | media({width: '150'})">
                    <button class="btn btn-link btn-block" @click.prevent.stop="product.slotImages.screenshots.splice(imageIndex, 1)">Remove</button>
                  </div>
                  <div class="card add-screenshot text-muted">
                    <a class="btn" @click="showToUploadScreens">
                        <i class="fa fa-4x fa-plus"></i>
                      </a>
                    <p class="text-muted m-2"> Add screenshot </p>
                  </div>
                  <media-file-input class="app-media" field="app-screenshots" @crop-upload-success="cropScreensUploadSuccess" v-model="showScreensInput" :width="800" :height="500" :url="uploadUrl" :no-circle="true" lang-type="en" :headers="headers" img-format="png"></media-file-input>
                </div>
                <div v-if="$v.product.slotImages.screenshots.$error" :class="{'invalid-feedback': $v.product.slotImages.screenshots.$error}">This field is required. Please be sure to check.</div>
                <small class="form-text text-muted">Upload screenshots of your app (required)</small>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Details page -->
      <div slot="slotDetails">
        <div class="row m-0 p-0 b-1">
          <div class="col-md-12 p-20">
            <!-- Overview -->
            <div class="form-group row m-b-15">
              <label for="overview-textarea" class="col-lg-3 col-form-label">Overview</label>
              <div class="col-lg-9">
                <textarea class="form-control" type="text" v-model="product.slotDetails.overview" rows="5" id="overview-textarea">
                </textarea>
                <div v-if="$v.product.slotDetails.overview.$error" :class="{'invalid-feedback': $v.product.slotDetails.overview.$error}">This field is required. Please be sure to check.</div>
                <small id="emailHelp" class="form-text text-muted">Summarize your app (required, 20-600 characters)</small>
              </div>
            </div>
            <!-- Applications/Clinical area/Modality -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label">Categories</label>
              <div class="col-lg-9">
                <div class="row">
                  <div class="col-md-4">
                    <label>Applications</label>
                    <div class="form-check" v-bind:key="application" v-for="application in applications">
                      <div class="checkbox checkbox-css checkbox-success">
                        <input :id="`app_${application}`" class="form-check-input" type="checkbox" :value="application" v-model="product.slotDetails.categories">
                        <label :for="`app_${application}`" class="text-capitalize">{{ application }}</label>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <label>Clinical area</label>
                    <div class="form-check" v-bind:key="ca" v-for="ca in clinicalAreas">
                      <div class="checkbox checkbox-css checkbox-success">
                        <input :id="`ca_${ca}`" class="form-check-input" type="checkbox" :value="ca" v-model="product.slotDetails.categories">
                        <label :for="`ca_${ca}`" class="text-capitalize">{{ ca }}</label>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <label>Modality</label>
                    <div class="form-check" v-bind:key="mod" v-for="mod in modalities">
                      <div class="checkbox checkbox-css checkbox-success">
                        <input :id="`mod_${mod}`" class="form-check-input" type="checkbox" :value="mod" v-model="product.slotDetails.categories">
                        <label :for="`mod_${mod}`" class="text-capitalize">{{ mod }}</label>
                      </div>
                    </div>
                  </div>
                </div>
                <small id="emailHelp" class="form-text text-muted">Check the categories for your app</small>
              </div>
            </div>
            <!-- Description -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label" for="description-textarea">Product description</label>
              <div class="col-lg-9">
                <vue-editor id="description-textarea" v-model="product.slotDetails.description" :editorToolbar="customToolbar"></vue-editor>
                <div v-if="$v.product.slotDetails.description.$error" :class="{'invalid-feedback': $v.product.slotDetails.description.$error}">This field is required. Please be sure to check.</div>
                <small class="form-text text-muted">Describe your app (required, min 20 char.)</small>
              </div>
            </div>
            <!-- KEY FEATURES -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label" for="features-textarea">Key features</label>
              <div class="col-lg-9">
                <vue-editor id="features-textarea" v-model="product.slotDetails.features" :editorToolbar="customToolbar"></vue-editor>
                <div v-if="$v.product.slotDetails.features.$error" :class="{'invalid-feedback': $v.product.slotDetails.features.$error}">This field is required. Please be sure to check.</div>
                <small class="form-text text-muted">Describe the key features (required, min 20 char.)</small>
              </div>
            </div>
            <!-- SYSTEM REQUIREMENT -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label" for="systemrequirement-textarea">System requirement</label>
              <div class="col-lg-9">
                <vue-editor id="systemRequirement" v-model="product.slotDetails.systemRequirement" :editorToolbar="customToolbar"></vue-editor>
                <small class="form-text text-muted">Describe the system requirement for this app</small>
              </div>
            </div>
            <!-- LAST UPDATES -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label" for="lastupdates-textarea">Last updates</label>
              <div class="col-lg-9">
                <vue-editor id="lastUpdates" v-model="product.slotDetails.lastUpdates" :editorToolbar="customToolbar"></vue-editor>
                <small class="form-text text-muted">Describe the last updates you do on this app</small>
              </div>
            </div>
            <!-- ADD VIDEO -->
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label" for="video-url-input">Add videos</label>
              <div class="col-lg-9">
                <div class="form-row m-b-5" v-for="(vid, index) in product.slotDetails.videos" :key="vid.src">
                  <div class="col-lg-5 m-r-5 p-0">
                    <input type="text" class="form-control" placeholder="Title" v-model="vid.title">
                  </div>
                  <div class="col-lg-5 m-r-5 p-0">
                    <input type="url" class="form-control" placeholder="Link" v-model="vid.src">
                  </div>
                  <div class="col">
                    <button class="btn btn-danger" @click.prevent.stop="product.slotDetails.videos.splice(index, 1)">
                      <i class="fa fa-minus text-white" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                <div class="form-row">
                  <div class="col-lg-5 m-r-5 p-0">
                    <input type="text" class="form-control" placeholder="Title" v-model="video.title">
                  </div>
                  <div class="col-lg-5 m-r-5 p-0">
                    <input type="url" class="form-control" placeholder="Link" v-model="video.src">
                  </div>
                  <div class="col">
                    <button class="btn btn-primary" @click.prevent.stop="addVideo" :disabled="!video.src || !video.title">
                      <i class="fa fa-plus text-white" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                <small class="form-text text-muted">Give the iframe url of a youtube video</small>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Publications page -->
      <div slot="slotPublications">
        <!-- Publications -->
        <div class="row m-0 p-0 b-1">
          <div class="col-md-12 p-20">
            <div class="form-group row m-b-15">
              <label class="col-lg-3 col-form-label">Add publication</label>
              <div class="col-lg-9">
                <div class="form-row m-b-5" v-for="(pub, index) in product.slotPublications.publications" :key="pub.link">
                  <div class="col-lg-5 m-r-5 p-0">
                    <input type="text" class="form-control" placeholder="Title" v-model="pub.title">
                  </div>
                  <div class="col-lg-5 m-r-5 p-0">
                    <input type="url" class="form-control" placeholder="Link" v-model="pub.link">
                  </div>
                  <div class="col">
                    <button class="btn btn-danger" @click.prevent.stop="product.slotPublications.publications.splice(index, 1)">
                      <i class="fa fa-minus text-white" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                <div class="form-row">
                  <div class="col-lg-5 m-r-5 p-0">
                    <input type="text" class="form-control" placeholder="Title" v-model="publication.title">
                  </div>
                  <div class="col-lg-5 m-r-5 p-0">
                    <input type="url" class="form-control" placeholder="Link" v-model="publication.link">
                  </div>
                  <div class="col">
                    <button class="btn btn-primary" @click.prevent.stop="addPublication" :disabled="!publication.link || !publication.title">
                      <i class="fa fa-plus text-white" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                <small class="form-text text-muted">Add publications</small>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Finish page -->
      <div slot="slotSubmit">
        <!-- Submit and send App -->
        <div class="row m-0 p-0 b-1">
          <div class="col-md-12 p-20">
            <div class="jumbotron m-b-0 text-center">
              <h2 class="text-inverse">Ready to Submit</h2>
              <p class="m-b-30 f-s-16">Your app is ready to be submitted to womia.io. You may change the metadata and binary until you click Submit App</p>
              <!--p><a href="" class="btn btn-primary btn-lg">Proceed to App View</a></p-->
            </div>
          </div>
        </div>
      </div>
    </wizard>
    <div class="confirm-app-modal">
      <modal v-if="showModal" @close="showModal = false">
        <h3 slot="header">Confirm</h3>
        <p slot="body">Please note that after submission, you can not modify your app until it has been validated by the administrator.
          <br>
          <br> Do you confirm the submission?</p>
        <div slot="footer">
          <a href="#" class="btn btn-outline-info" data-dismiss="modal" @click="showModal = false">Cancel</a>
          <a href="#" class="btn btn-primary" data-dismiss="modal" @click="submit()">Submit</a>
        </div>
      </modal>
    </div>
    <div class="info-app-modal">
      <modal v-if="showModalInfo" @close="showModalInfo = false">
        <h3 slot="header" class="text-muted"><i class="fa fa-2x fa-info-circle"></i> Information</h3>
        <p slot="body">Currently, we manage free Apps only. Please <a href="/beta/contact-us">contact us</a> if you have other requirements.</p>
        <div slot="footer">
          <a href="#" class="btn btn-outline-info" data-dismiss="modal" @click="showModalInfo = false">Continue</a>
        </div>
      </modal>
    </div>
  </form>
</template>
<script>
import Vue from 'vue'
import _ from 'lodash'
import { validationMixin } from 'vuelidate'
import UploadFileInput from '@/components/UploadFileInput'
import MediaFileUpload from 'vue-image-crop-upload'
import Wizard from '@/components/Wizard'
import Dialog from '@/components/Dialog'
import { VueEditor } from 'vue2-editor'
import { required, minLength, maxLength, url, requiredIf } from 'vuelidate/lib/validators'
/* eslint-disable */
export default {
  name: 'EditProductDeveloper',
  components: { 'app-file-input': UploadFileInput, 'media-file-input': MediaFileUpload, 'vue-editor': VueEditor, 'wizard': Wizard, 'modal': Dialog },
  data() {
    return {
      message: null,
      showModal: false,
      showModalInfo: false,
      steps: [{
          label: 'General',
          description: '',
          slot: 'slotGeneral'
        },
        {
          label: 'Images',
          description: '',
          slot: 'slotImages'
        },
        {
          label: 'Details',
          description: '',
          slot: 'slotDetails'
        },
        {
          label: 'Publications',
          description: '',
          slot: 'slotPublications'
        },
        {
          label: 'Submit App',
          description: '',
          slot: 'slotSubmit'
        }
      ],
      publication: {},
      video: {},
      customToolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ header: [4, 5, 6, false] }],
        [{ align: [] }],
        ['clean']
      ],
      product: {
        slotGeneral: {
          technicalDetails: {}
        },
        slotImages: {
          logo: {}
        },
        slotDetails: {},
        slotPublications: {}
      },
      applications: [
        'workflow',
        'visualisation',
        'quantification',
        'reporting'
      ],
      modalities: ['mri', 'ultrasound', 'xray', 'ct', 'pet'],
      clinicalAreas: ['cardiology', 'vascular', 'neurology', 'oncology', 'dental'],
      showLogoInput: false,
      showScreensInput: false,
      headers: {
        smail: '*_~'
      },
      uploadUrl: `${Vue.http.options.root}/media`,
      imgLogoData: {
        src: '/beta/static/app-logo-default-300x300.png',
        hasImg: false
      }
    }
  },
  mixins: [validationMixin],
  validations: {
    product: {
      slotGeneral: {
        name: {
          required,
          minLength: minLength(5),
          maxLength: maxLength(30)
        },
        app: {
          required,
          zipFile(appModel) {
            if (appModel && appModel.type === 'application/zip') {
              return true
            }
            return false
          }
        },
        technicalDetails: {
          version: {
            required
          }
        },
        viewer: {
          required
        },
        externalLinkDist: {
          required: requiredIf(function () {
            if (this.externaldistributed === 'yes') {
              return true
            }
            return false
          }), 
          url
        }
        },
      slotImages: {
        logo: {
          required
        },
        screenshots: {
          required
        }
      },
      slotDetails: {
        overview: {
          required,
          minLength: minLength(20),
          maxLength: maxLength(600)
        },
        description: {
          required,
          minLength: minLength(20)
        },
        features: {
          required,
          minLength: minLength(20)
        }
      }
    }
  },
  computed: {
    externaldistributed: function()  {
      return this.product.slotGeneral.externalLinkDist ? 'yes' : 'no' 
    }
  },
  created: function () {
    if (this.$route.params.slug) {
      this.fetchData()
    } else {
      this.showModalInfo = true
      this.product = {
        slotGeneral: {
          externalLinkDist: null,
          certifications: [],
          viewer:[],
          technicalDetails: {
            size: null,
            version: null,
            createdDate: null,
            updatedDate: null
          },
          price: 0
        },
        slotImages: {
          screenshots: []
        },
        slotDetails: {
          categories: [],
          videos: []
        },
        slotPublications: {
          publications: []
        }
      }
    }
  },
  methods: {
    hideMessages() {
      this.message = null
    },
    showErrorMessage(message) {
      this.message = message
    },
    fetchData() {
      this.$http.get(`product/dev/${this.$route.params.slug}`).then(
        response => {
          this.populateProduct(response.data)
        },
        err => {
          // error callback
          console.log(err)
        }
      )
    },
    populateProduct(product) {
      /* set general params */
      this.product._id = product._id
      this.product.slug = product.slug
      this.product.slugRef = product.slugRef
      this.product.slotGeneral = {
        name: product.name,
        app: product.app,
        technicalDetails: product.technicalDetails || {},
        certifications: product.certifications,
        viewer: product.viewer,
        externalLinkDist: product.externalLinkDist,
        price: product.price
      }
      /* set images params */
      this.product.slotImages = {
        logo: product.logo,
        screenshots: product.screenshots
      }
      if (product.logo) this.imgLogoData = { src: this.$options.filters.media(product.logo.slug, { width: '250' }), hasImg: true }
      /* set details params */
      this.product.slotDetails = {
        overview: product.overview,
        categories: product.categories,
        description: product.description,
        features: product.features,
        systemRequirement: product.systemRequirement,
        lastUpdates: product.lastUpdates,
        videos: product.videos || [],
        userGuide: product.userGuide
      }
      /* set publications params */
      this.product.slotPublications = {
        publications: product.publications || []
      }
    },
    isModified() {
      let updateProduct = this.prepareProduct(this.product)
      _.map(_.keys(updateProduct), (key) => {
        if (!updateProduct[key]) updateProduct = _.omit(updateProduct, key)
      })
      updateProduct.technicalDetails = _.pick(updateProduct.technicalDetails, 'version')
      return this.$http.get(`product/dev/${this.product.slug}`).then(
        resp => {
          let oldProduct = resp.data
          oldProduct.technicalDetails = _.pick(oldProduct.technicalDetails, 'version')
          oldProduct = _.pick(oldProduct, _.keys(updateProduct))
          return !_.isEqual(oldProduct, updateProduct)
        })
    },
    prepareProduct(product) {
      return {
        _id: product._id,
        slugRef: product.slugRef,
        name: product.slotGeneral.name,
        app: product.slotGeneral.app,
        technicalDetails: {
          size: product.slotGeneral.app.binSize,
          version: product.slotGeneral.technicalDetails.version
        },
        certifications: product.slotGeneral.certifications,
        viewer: product.slotGeneral.viewer,
        externalLinkDist: product.slotGeneral.externalLinkDist,
        price: product.slotGeneral.price,
        logo: product.slotImages.logo,
        screenshots: product.slotImages.screenshots,
        overview: product.slotDetails.overview,
        categories: product.slotDetails.categories,
        description: product.slotDetails.description,
        features: product.slotDetails.features,
        systemRequirement: product.slotDetails.systemRequirement,
        lastUpdates: product.slotDetails.lastUpdates,
        videos: product.slotDetails.videos,
        publications: product.slotPublications.publications
      }
    },
    action(promise) {
      this.hideMessages()
      promise.then(
        response => {
          const redirectToMyApps = {
            name: 'ListProductDeveloper'
          }
          this.$router.push(redirectToMyApps)
        },
        () => {
          // error callback
          this.showErrorMessage('Sorry but an error occurred when submitting form')
        }
      )
    },
    create() {
      return this.$http.put(`product/dev`, this.prepareProduct(this.product))
    },
    update() {
      let updateProduct = this.prepareProduct(this.product)
      console.log('updateProduct',updateProduct)
      updateProduct.technicalDetails.updatedDate = new Date()
      return this.$http.post(`product/dev/${this.product.slug}`, updateProduct)
    },
    submit() {
      // use popup for confirmation
      let promise
      if (!this.product._id) {
        promise = this.$http.put(`product/dev`, this.prepareProduct(this.product))
      } else {
        promise = this.$http.post(`product/dev/${this.product.slug}`, this.prepareProduct(this.product))
      }

      promise.then(
        response => {
          this.action(
            this.$http.post(`product/dev/${response.body.slug}/submit-my-app`, { '_id': response.body._id })
          )
        },
        () => {
          // error callback
          this.showErrorMessage('Sorry but an error occurred when submitting form')
        }
      )
      // Hide Modal
      this.showModal = false
    },
    addVideo() {
      this.product.slotDetails.videos.push(this.video)
      this.video = {}
    },
    addPublication() {
      this.product.slotPublications.publications.push(this.publication)
      this.publication = {}
    },
    nextWizard(currentPage) {
      this.hideMessages()
      /* go next if all field are validate */
      const currentSlot = this.steps[currentPage].slot

      if (!this.$v.product[currentSlot] || !this.$v.product[currentSlot].$invalid) {
        if (this.product._id) {
          this.update().then(resp => {
            this.populateProduct(resp.data)
            this.$refs.wizard.goNext(true)
          }, (err) => {
            // error callback
            if (err.body.message.indexOf('name.app.already.used') > -1) {
              this.showErrorMessage('Sorry, the same name was already used for another App')
            } else {
              this.showErrorMessage('Sorry but an error occurred when submitting form')
            }
          })
          /*
          this.isModified().then(isModified => {
            if (isModified) {
              this.update().then(resp => {
                this.populateProduct(resp.data)
                this.$refs.wizard.goNext(true)
              }, (err) => {
                // error callback
                if (err.body.message.indexOf('name.app.already.used') > -1) {
                  this.showErrorMessage('Sorry, the same name was already used for another App')
                } else {
                  this.showErrorMessage('Sorry but an error occurred when submitting form')
                }
              })
            } else this.$refs.wizard.goNext(true)
          }, () => {
            this.showErrorMessage('Sorry but an error occurred when submitting form')
          })
          */
        } else {
          this.create().then(resp => {
              this.populateProduct(resp.data)
              this.$refs.wizard.goNext(true)
            },
            (err) => {
              // error callback
              if (err.body.message.indexOf('slug_1 dup key') > -1) {
                this.showErrorMessage('Sorry, the same name was already used for another App')
              } else {
                this.showErrorMessage('Sorry but an error occurred when submitting form')
              }
            })
        }
      } else {
        this.showErrorMessage('Please make sure all required fields are filled out correctly')
        this.$v.product[currentSlot].$touch()
        return false
      }
    },
    backWizard(currentPage) {
      this.hideMessages()
      return true
    },
    submitApp(currentPage) {
      this.showModal = true
      return true
    },
    showToUploadLogo() {
      this.showLogoInput = true
    },
    showToUploadScreens() {
      this.showScreensInput = true
    },
    cropLogoSuccess(imgDataUrl, field) {
      this.imgLogoData = { src: imgDataUrl, hasImg: true }
    },
    cropLogoUploadSuccess(jsonData, field) {
      this.product.slotImages.logo = jsonData
    },
    cropScreensUploadSuccess(jsonData, field) {
      this.product.slotImages.screenshots.push(jsonData)
    }
  }
}
</script>
<style lang="scss">
.app-media {
  .app-file .box {
    display: inline-block;
    padding: 4px;
    min-width: 100px;
  }
  .vicp-wrap .vicp-preview {
    height: 250px !important;
  }
  .vicp-wrap .vicp-preview-item {
    padding: 5px;
    width: 200px !important;
    height: 200px !important;
  }
  .vicp-preview-item img {
    width: 200px !important;
    height: auto !important;
  }
}

.card.add-screenshot {
  border: none;
  a {
    width: 110px;
    height: 110px;
    padding: 0.25rem;
    border: 1px dashed;
  }
}

@mixin vertical-align($position: relative) {
  position: $position;
  top: 50%;
  -webkit-transform: translateY(-50%);
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
}

.card.add-screenshot i {
  @include vertical-align();
}

</style>
<style scoped>
.form-group>label {
  font-size: 14px;
}

.card.logo {
  width: 252px;
  height: auto;
}

.card.logo img {
  widows: 250px;
  height: 250px;
}

.img-thumbnail {
  background: none;
}

.checkbox.checkbox-css,
.radio.radio-css {
  display: inline;
}

.screenshots-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

</style>



// WEBPACK FOOTER //
// src/pages/developer/EditProductDeveloper.vue